import React, { useState } from "react";
import { Button, message, Popconfirm, Space, Tooltip, Upload, Modal, Form, Select } from "antd";
import { UploadOutlined, DownloadOutlined, FileExcelOutlined, DeleteOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import TableButtonActions from "../../../../components/common/TableButtonActions";
import { useBeneficiariesStore } from "../../../../config/stores";
import AntdTable from "../../../../components/common/AntdTable";
import TableTitle from "../../../../components/common/TableTitle";
import { searchTable } from "../../../../config/constants";

const { Option } = Select;

const getUniqueValues = (data, key) =>
  [...new Set((data || []).map((r) => r[key]).filter(Boolean))].map((v) => ({ text: v, value: v }));

const BeneficiariesTable = ({ onFilteredDataChange = () => {} }) => {
  const [filterTable, setFilterTableNull] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [uploadModal, setUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadForm] = Form.useForm();
  const [selectedRows, setSelectedRows] = useState([]);

  const beneficiariesStore = useBeneficiariesStore();
  const data = beneficiariesStore.beneficiaries || [];
  const { projects } = useSelector((state) => state.projectsState);
  const allProjects = [
    ...(projects?.activeProjects || []),
    ...(projects?.completedProjects || []),
  ];

  const search = (value) => {
    const filtered = searchTable(data, value);
    setFilterTableNull(filtered);
    onFilteredDataChange(filtered || data);
  };

  const handleTableChange = (_, __, ___, extra) => {
    onFilteredDataChange(extra.currentDataSource);
  };

  const confirmDelete = () => {
    beneficiariesStore.deleteBeneficiary(deleteId);
    setDeleteId(null);
  };

  const viewDetails = (id) => {
    beneficiariesStore.setShowViewBeneficiaryDetails(true);
    beneficiariesStore.getBeneficiaryDetails(id);
  };

  const deleteRecord = (id) => setDeleteId(id);

  const confirmBulkDelete = async () => {
    const count = selectedRows.length;
    const success = await beneficiariesStore.bulkDeleteBeneficiaries(selectedRows);
    if (success) {
      message.success(`${count} beneficiary(ies) deleted successfully`);
      setSelectedRows([]);
    } else {
      message.error("Bulk delete failed. Please try again.");
    }
  };

  const updateRecord = (record) => {
    beneficiariesStore.setUpdateBeneficiaryData(record);
    beneficiariesStore.setCreating(true, false, record);
  };

  const handleUploadSubmit = async () => {
    const values = await uploadForm.validateFields();
    if (!uploadFile) return message.error("Please select an Excel file");
    const success = await beneficiariesStore.uploadBeneficiaries(uploadFile, values.project_id);
    if (success) {
      setUploadModal(false);
      setUploadFile(null);
      uploadForm.resetFields();
      message.success("Upload complete");
    } else {
      message.error("Upload failed. Please check the file and try again.");
    }
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      filterSearch: true,
      filters: getUniqueValues(data, "name"),
      onFilter: (value, record) => record.name === value,
    },
    {
      title: "Project",
      dataIndex: ["project", "name"],
      filters: getUniqueValues(data.map((r) => ({ project_name: r.project?.name })), "project_name"),
      onFilter: (value, record) => record.project?.name === value,
    },
    {
      title: "Gender",
      dataIndex: "gender",
      filters: getUniqueValues(data, "gender"),
      onFilter: (value, record) => record.gender === value,
    },
    {
      title: "Age Category",
      dataIndex: "age_category",
      filters: getUniqueValues(data, "age_category"),
      onFilter: (value, record) => record.age_category === value,
    },
    {
      title: "Year Enrolled",
      dataIndex: "year_enrolled",
      filters: getUniqueValues(data, "year_enrolled"),
      onFilter: (value, record) => record.year_enrolled === value,
    },
    {
      title: "Nationality",
      dataIndex: "nationality",
      filters: getUniqueValues(data, "nationality"),
      onFilter: (value, record) => record.nationality === value,
    },
    {
      title: "Location",
      dataIndex: "location",
      filters: getUniqueValues(data, "location"),
      onFilter: (value, record) => record.location === value,
    },
    {
      title: "Category",
      dataIndex: "category",
      filters: getUniqueValues(data, "category"),
      onFilter: (value, record) => record.category === value,
    },
    {
      title: "Disability Status",
      dataIndex: "disability_status",
      filters: [
        { text: "Yes", value: true },
        { text: "No", value: false },
      ],
      onFilter: (value, record) => Boolean(record.disability_status) === value,
      render: (value) => (value ? "Yes" : "No"),
    },
    {
      title: "Actions",
      key: "actions",
      responsive: ["md"],
      render: (record) => (
        <TableButtonActions
          record={record}
          confirmDelete={confirmDelete}
          deleteLoading={beneficiariesStore.deletingBeneficiary}
          viewDetails={viewDetails}
          updateRecord={updateRecord}
          deleteId={deleteId}
          deleteRecord={deleteRecord}
        />
      ),
    },
  ];

  const openAddModal = () => beneficiariesStore.setCreating(true, true, {});
  const refreshTable = () => beneficiariesStore.getBeneficiaries();

  const extraButtons = (
    <Space>
      {selectedRows.length > 0 && (
        <Popconfirm
          okText="Yes"
          cancelText="No"
          title={`Delete ${selectedRows.length} selected beneficiary(ies)?`}
          onConfirm={confirmBulkDelete}
        >
          <Button
            danger
            type="primary"
            icon={<DeleteOutlined />}
            loading={beneficiariesStore.bulkDeletingBeneficiaries}
          >
            Delete Selected ({selectedRows.length})
          </Button>
        </Popconfirm>
      )}
      <Tooltip title="Download blank Excel template">
        <Button
          icon={<FileExcelOutlined />}
          style={{ borderColor: "#52c41a", color: "#52c41a" }}
          onClick={() => beneficiariesStore.downloadTemplate()}
        >
          Template
        </Button>
      </Tooltip>
      <Tooltip title="Upload beneficiaries from Excel">
        <Button
          icon={<UploadOutlined />}
          type="primary"
          ghost
          onClick={() => setUploadModal(true)}
        >
          Upload Excel
        </Button>
      </Tooltip>
      <Tooltip title="Export current data to Excel">
        <Button
          type="primary"
          icon={<DownloadOutlined />}
          onClick={() => beneficiariesStore.exportBeneficiaries()}
        >
          Export
        </Button>
      </Tooltip>
    </Space>
  );

  return (
    <div>
      <AntdTable
        loading={beneficiariesStore.gettingBeneficiaries}
        selectableRow
        selectedRows={selectedRows}
        setSelectedRows={setSelectedRows}
        title={() => (
          <TableTitle
            setFilterTableNull={setFilterTableNull}
            openAddModal={openAddModal}
            refreshTable={refreshTable}
            search={search}
            exportRecords={null}
            rightActions={extraButtons}
          />
        )}
        pageSize={20}
        data={filterTable === null ? data : filterTable}
        columns={columns}
        onChange={handleTableChange}
      />

      <Modal
        open={uploadModal}
        title="Upload Beneficiaries from Excel"
        onCancel={() => {
          setUploadModal(false);
          setUploadFile(null);
          uploadForm.resetFields();
        }}
        onOk={handleUploadSubmit}
        okText="Upload"
        confirmLoading={beneficiariesStore.creatingBeneficiary}
      >
        <Form form={uploadForm} layout="vertical">
          <Form.Item
            name="project_id"
            label="Project (fallback)"
            extra="Used only for rows in the Excel that have no project selected"
          >
            <Select placeholder="Select fallback project" showSearch optionFilterProp="children" allowClear>
              {allProjects.map((p) => (
                <Option key={p.id} value={p.id}>{p.name}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label="Excel File" required>
            <Upload
              accept=".xlsx,.xls"
              beforeUpload={(file) => { setUploadFile(file); return false; }}
              maxCount={1}
              onRemove={() => setUploadFile(null)}
            >
              <Button icon={<UploadOutlined />}>Select File</Button>
            </Upload>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default BeneficiariesTable;
